import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { useRef, useState } from "react";
import {
  ClientManagementIcon,
  FeedbackIcon,
  LogoutIcon,
  PersonInCircleIcon,
  PersonWithPenIcon,
  PlusIcon,
  ReviewsIcon,
} from "../images";
import { useOutsideClick } from "../../hooks/useOutSideClick";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { AppRoutes, PermissionRoles } from "../../types/enums";
import { useGetUserQuery } from "../../api";
import { ProfileAvatar } from "./ProfileAvatar";
import { useAppSelector, useLogout } from "../../hooks";
import { ExportEmailsButton } from "./ExportEmailsButton";
import { MenuNavButton } from "../ui/buttons";
import { AddClientModal, FeedbackModal } from "../ui/modal";
import { ClientModeSwitcher } from "../ui/ClientModeSwitcher";
import { selectPermissions } from "../../features/permissionsSlice";
import { PermissionRoleMapper } from "../company";

type Props = {
  role?: PermissionRoles;
};

export const ProfileButton = ({ role }: Props) => {
  const logout = useLogout();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isFeedbackModal, setIsFeedbackModal] = useState<boolean>(false);
  const [isAddClientModal, setIsAddClientModal] = useState<boolean>(false);

  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    isAllowedClientViewDemo,
    isAllowedShareAccess,
    isAllowedShareViewListInternalUsers,
  } = useAppSelector(selectPermissions);

  const { data: user } = useGetUserQuery();

  const closeHandler = () => {
    setIsOpen(false);
  };

  useOutsideClick({
    elementRef: dropdownRef,
    onOutsideClick: closeHandler,
    isOpened: isOpen,
    wrapperRef: dropdownWrapperRef,
  });

  const logoutHandler = () => {
    logout();
  };

  const isAdmin = user?.roles.includes(PermissionRoles.admin);

  return (
    <Stack ref={dropdownWrapperRef} gap="16px" alignItems="flex-start">
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: 0,
          border: "none",
          cursor: "pointer",
          background: "none",
          display: "flex",
        }}
      >
        <ProfileAvatar
          avatar_url={user?.avatar_url}
          height="42px"
          width="42px"
        />
      </button>

      {isOpen && (
        <Stack
          ref={dropdownRef}
          sx={{
            backgroundColor: "white",
            position: "absolute",
            zIndex: 101,
            right: 0,
            top: 80,
            boxShadow: "0px 4px 15px 0px #00000040",
            borderRadius: "8px",
            padding: "24px 0 8px 0",
            width: "260px",
            gap: "8px",
          }}
        >
          <IconButton
            onClick={() => setIsOpen(false)}
            size="small"
            sx={{
              ml: "auto",
              color: "#828282",
              position: "absolute",
              top: "8px",
              right: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Stack flexDirection={"row"} gap={"12px"} padding={"0 16px"}>
            <ProfileAvatar avatar_url={user?.avatar_url} />
            <Stack gap="8px">
              <Typography
                variant="h4"
                color="custom.textDarkBlue"
                fontSize="16px"
                lineHeight="20px"
                fontWeight={500}
                sx={{ letterSpacing: "0.3px", textAlign: "center" }}
              >
                {`${user?.first_name} ${user?.last_name}`}
              </Typography>
              <Typography variant="caption" color="#4C545B" lineHeight="18px">
                {`${user?.company_name}`}
              </Typography>
              {role && <PermissionRoleMapper role={role} />}
            </Stack>
          </Stack>
          <Divider
            style={{ paddingTop: "8px", alignSelf: "stretch" }}
            flexItem
          />
          {isAllowedClientViewDemo && (
            <>
              <Stack
                sx={{
                  padding: "0 8px",
                }}
              >
                <ClientModeSwitcher clientView={!!user?.client_view} />
              </Stack>

              <Divider style={{ alignSelf: "stretch" }} flexItem />
            </>
          )}
          <Stack
            sx={{
              padding: "0 8px",
            }}
          >
            <MenuNavButton
              handler={() => {
                navigate(AppRoutes.PROFILE);
                setIsOpen(false);
              }}
              text="My Profile"
              icon={<PersonInCircleIcon color={"#1C3C6C"} />}
            />
          </Stack>

          <Divider style={{ alignSelf: "stretch" }} flexItem />

          {isAllowedShareViewListInternalUsers && (
            <>
              <Stack
                sx={{
                  padding: "0 8px",
                }}
              >
                <MenuNavButton
                  handler={() => {
                    navigate(AppRoutes.PERMISSIONS);
                    setIsOpen(false);
                  }}
                  text="Permissions Management"
                  icon={<PersonWithPenIcon />}
                />
              </Stack>

              <Divider style={{ alignSelf: "stretch" }} flexItem />
            </>
          )}

          <Stack gap={"8px"} paddingX={"8px"}>
            {isAllowedShareAccess && (
              <MenuNavButton
                handler={() => {
                  setIsAddClientModal((prev) => !prev);
                  setIsOpen(false);
                }}
                text="Add New Client User"
                icon={<PlusIcon color={"#1C3C6C"} />}
              />
            )}
            {isAllowedShareAccess && (
              <MenuNavButton
                handler={() => {
                  setIsOpen(false);
                  navigate(AppRoutes.CLIENTS);
                }}
                text="Client User Management"
                icon={<ClientManagementIcon color={"#1C3C6C"} />}
              />
            )}
            {isAdmin && <ExportEmailsButton />}
            <MenuNavButton
              handler={() => {
                setIsOpen(false);
                setIsFeedbackModal(true);
              }}
              text="Submit Feedback"
              icon={<FeedbackIcon color={"#1C3C6C"} />}
            />

            {isAdmin && (
              <MenuNavButton
                handler={() => {
                  setIsOpen(false);
                  navigate(AppRoutes.REVIEWS);
                }}
                text="Reviews"
                icon={<ReviewsIcon color={"#1C3C6C"} />}
              />
            )}
          </Stack>

          <Divider style={{ alignSelf: "stretch" }} flexItem />
          <Stack
            sx={{
              padding: "0 8px",
            }}
          >
            <MenuNavButton
              handler={logoutHandler}
              text="Log Out"
              icon={<LogoutIcon color={"#1C3C6C"} />}
            />
          </Stack>
        </Stack>
      )}

      {isFeedbackModal && (
        <FeedbackModal
          isOpen={isFeedbackModal}
          setIsOpen={setIsFeedbackModal}
        />
      )}

      {isAddClientModal && (
        <AddClientModal onClose={() => setIsAddClientModal(false)} />
      )}
    </Stack>
  );
};
